import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Collapse, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { Trans, useTrans } from '@libs/i18n';
import { AssessmentDomainDetail, DomainStandardDocumentDetail, PreviousDomainSummary } from '@libs/types';
import { computeMissingStandardDocuments } from '@libs/utils';
import {
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  FileXIcon,
  PencilIcon,
  ProhibitIcon,
  QuestionIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';

import { DocumentItem } from './DocumentItem';
import { MissingStandardDocumentItem } from './MissingStandardDocumentItem';

interface DomainSummaryItemProps {
  assessmentId: string;
  domain: AssessmentDomainDetail;
  /**
   * Parent-version snapshot of THIS domain. `undefined` for v1 assessments
   * or when the parent has no AI results. When present, the row renders
   * the parent score, edited badge, missing-signal pill, and the removed
   * documents section in expanded detail.
   */
  previousDomainSummary?: PreviousDomainSummary;
  /** Parent assessment version (e.g. 3 → "V3"). `undefined` for v1. */
  parentVersion?: number;
  onDownloadDocument: (documentId: string) => void;
}

export const DomainSummaryItem: FC<DomainSummaryItemProps> = observer(
  ({ assessmentId, domain, previousDomainSummary, parentVersion, onDownloadDocument }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const documentCount = domain.documents?.length || 0;
    const domainName = domain.frameworkDomain?.name || '';
    const isNotApplicable = !domain.isApplicable;
    const isSkipped = !isNotApplicable && domain.isSkipped;

    // Not-applicable domains contribute zero missing docs; the domain itself
    // is opted-out, so the standard catalog is irrelevant. Skipped applicable
    // domains surface the entire catalog as "missing" because the user agreed
    // the domain applies but uploaded no evidence.
    const missingStandardDocuments: DomainStandardDocumentDetail[] = isNotApplicable
      ? []
      : isSkipped
        ? ((domain.frameworkDomain?.standardDocuments ?? []) as DomainStandardDocumentDetail[])
        : computeMissingStandardDocuments(domain);
    const missingCount = missingStandardDocuments.length;

    // ──────────────────────────────────────────────────────────────────────
    // Re-assessment derivations — only meaningful when previousDomainSummary
    // is defined (assessment.version > 1 with a parent that has AI results).
    // ──────────────────────────────────────────────────────────────────────
    const isReassessment = !!previousDomainSummary;
    const isEdited = isReassessment && domain.isModifiedFromPrevious;
    const parentMissingCount = previousDomainSummary?.missingDocuments.length ?? 0;
    const parentScorePercentage = previousDomainSummary?.results.maturity.percentage ?? null;
    const removedDocuments = previousDomainSummary?.removedDocuments ?? [];

    // Direction of the change in missing-doc count vs parent. Negative = better.
    const missingDelta = isReassessment ? missingCount - parentMissingCount : 0;
    const missingDirection: 'better' | 'worse' | 'flat' =
      missingDelta < 0 ? 'better' : missingDelta > 0 ? 'worse' : 'flat';

    const handleToggle = () => setIsExpanded((prev) => !prev);

    const handleEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(routes.assessmentDomain(assessmentId, domain.id));
    };

    // Left icon badge reflects domain status. Edited wins over everything
    // else on re-assessment rows — it's the most important diff signal.
    // After that: not-applicable (a stable opt-out) > skipped > missing.
    const leftBadge = (() => {
      if (isEdited) {
        return (
          <Box sx={{ backgroundColor: 'purple.100', p: 0.75, borderRadius: '100%', display: 'flex' }}>
            <PencilIcon color={theme.palette.purple[700]} size={20} weight="fill" />
          </Box>
        );
      }
      if (isNotApplicable) {
        return (
          <Box sx={{ backgroundColor: 'grey.100', p: 0.75, borderRadius: '100%', display: 'flex' }}>
            <ProhibitIcon color={theme.palette.text.secondary} size={20} weight="fill" />
          </Box>
        );
      }
      if (isSkipped) {
        return (
          <Box sx={{ backgroundColor: 'warning.lighter', p: 0.75, borderRadius: '100%', display: 'flex' }}>
            <QuestionIcon color={theme.palette.warning.main} size={20} weight="fill" />
          </Box>
        );
      }
      if (missingCount > 0) {
        return (
          <Box sx={{ backgroundColor: 'error.lighter', p: 0.75, borderRadius: '100%', display: 'flex' }}>
            <WarningCircleIcon color={theme.palette.error.main} size={20} weight="fill" />
          </Box>
        );
      }
      return (
        <Box sx={{ backgroundColor: 'green.200', p: 0.75, borderRadius: '100%', display: 'flex' }}>
          <CheckCircleIcon color={theme.palette.green[700]} size={20} />
        </Box>
      );
    })();

    const notApplicableStatusBadge = isNotApplicable ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ProhibitIcon color={theme.palette.text.secondary} size={16} weight="fill" />
        <Typography variant="textXS_medium" sx={{ color: 'text.secondary' }}>
          <Trans i18nKey="assessments.overview.domainNotApplicableBadge" />
        </Typography>
      </Box>
    ) : null;

    const skippedStatusBadge = isSkipped ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <QuestionIcon color={theme.palette.warning.main} size={16} weight="fill" />
        <Typography variant="textXS_medium" sx={{ color: 'warning.main' }}>
          <Trans i18nKey="assessments.summary.skipped" />
        </Typography>
      </Box>
    ) : null;

    const missingDocumentsBadge = missingCount > 0 ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <WarningCircleIcon color={theme.palette.error.main} size={16} weight="fill" />
        <Typography variant="textXS_medium" sx={{ color: 'error.main' }}>
          <Trans i18nKey="assessments.summary.missingDocumentsBadge" count={missingCount} />
        </Typography>
      </Box>
    ) : null;

    // Re-assessment "Edited for V{n}" pill. Sits in the right-column with
    // the missing-docs badge so users can see the row's diff state at a glance.
    const editedBadge = isEdited ? (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          backgroundColor: 'purple.100',
          border: `1px solid ${theme.palette.purple[200]}`,
          color: 'purple.700',
        }}
      >
        <PencilIcon size={11} weight="fill" />
        <Typography variant="textXS_medium" sx={{ color: 'purple.700' }}>
          <Trans i18nKey="assessments.summary.editedForVersion" values={{ version: parentVersion }} />
        </Typography>
      </Box>
    ) : null;

    // Missing-signal pill: shows direction of change in missing-doc count
    // (e.g. "2 fewer missing" green, "1 more missing" red). Hidden when the
    // count is unchanged or there's no parent to compare against.
    const missingSignalBadge = isReassessment && missingDirection !== 'flat' ? (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          backgroundColor: missingDirection === 'better' ? 'green.100' : 'error.lighter',
          border: `1px solid ${
            missingDirection === 'better' ? theme.palette.green[200] : theme.palette.error.light
          }`,
          color: missingDirection === 'better' ? 'green.700' : 'error.main',
        }}
      >
        {missingDirection === 'better' ? (
          <CaretUpIcon size={10} weight="bold" />
        ) : (
          <CaretDownIcon size={10} weight="bold" />
        )}
        <Typography
          variant="textXS_medium"
          sx={{ color: missingDirection === 'better' ? 'green.700' : 'error.main' }}
        >
          {missingDirection === 'better' ? (
            <Trans
              i18nKey="assessments.summary.fewerMissing"
              values={{ count: Math.abs(missingDelta) }}
            />
          ) : (
            <Trans
              i18nKey="assessments.summary.moreMissing"
              values={{ count: missingDelta }}
            />
          )}
        </Typography>
      </Box>
    ) : null;

    // Row-level tint when missing-doc count changed. Mirrors the design's
    // green/red row backgrounds; subtle so the rest of the row stays readable.
    const rowTintSx =
      missingDirection === 'better'
        ? { backgroundColor: 'green.100' }
        : missingDirection === 'worse'
          ? { backgroundColor: 'error.lighter' }
          : {};

    return (
      <Box sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: 2, ...rowTintSx }}>
        {/* Collapsed header */}
        <Box
          onClick={handleToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.25,
            px: 0.5,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
            borderRadius: 2,
          }}
        >
          {/* Left section: icon + name + doc count + (parent score, on re-assessment) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
            {leftBadge}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="textSM_regular" sx={{ color: 'text.primary' }}>
                {domainName}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                  {documentCount} {t('assessments.summary.documents').toLowerCase()}
                </Typography>
                {isReassessment && parentScorePercentage !== null && parentVersion !== undefined && (
                  <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                    <Trans
                      i18nKey="assessments.summary.parentDomainScore"
                      values={{
                        version: parentVersion,
                        percentage: Math.round(parentScorePercentage),
                      }}
                    />
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Right section: status badges + edit + caret */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}
          >
            {editedBadge}
            {missingSignalBadge}
            {notApplicableStatusBadge}
            {skippedStatusBadge}
            {missingDocumentsBadge}
            {/* Edit */}
            <Button
              variant="outlined"
              onClick={handleEdit}
              endIcon={<PencilIcon size={16} />}
              aria-label={t('app.assessments.ariaLabel.editDomain')}
              sx={{
                color: 'text.primary',
                backgroundColor: 'transparent',
                borderColor: 'grey.500',
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
              }}
            >
              <Trans i18nKey="assessments.summary.edit" />
            </Button>

            {/* Caret */}
            <CaretDownIcon
              size={20}
              color={theme.palette.text.secondary}
              style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
        </Box>

        {/* Expanded content */}
        <Collapse in={isExpanded}>
          <Box sx={{ px: 5, py: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Not-applicable empty state — replaces docs / missing list entirely */}
              {isNotApplicable && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    p: 2,
                    border: `1px dashed ${theme.palette.grey[300]}`,
                    backgroundColor: 'grey.50',
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'grey.100',
                      color: 'text.secondary',
                      border: `1px solid ${theme.palette.grey[300]}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ProhibitIcon size={16} />
                  </Box>
                  <Box>
                    <Typography
                      variant="textSM_medium"
                      sx={{ color: 'text.primary', display: 'block', mb: 0.5 }}
                    >
                      <Trans i18nKey="assessments.overview.domainNotApplicableTitle" />
                    </Typography>
                    <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
                      <Trans i18nKey="assessments.overview.domainNotApplicableDescription" />
                    </Typography>
                  </Box>
                </Box>
              )}

              {!isNotApplicable && !isSkipped && (
                <>
                  {domain.documents && domain.documents.length > 0 ? (
                    domain.documents.map((doc) => (
                      <DocumentItem
                        key={doc.documentId}
                        document={doc}
                        domainName={domainName}
                        parentVersion={parentVersion}
                        onDownload={onDownloadDocument}
                      />
                    ))
                  ) : (
                    <Typography variant="textSM_regular" sx={{ color: 'text.tertiary', py: 2 }}>
                      <Trans i18nKey="assessments.summary.noDocuments" />
                    </Typography>
                  )}
                </>
              )}

              {/* Removed-since-parent docs section — only when re-assessing
                  and the domain is still in play (not-applicable opts out). */}
              {!isNotApplicable && removedDocuments.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, mt: 0.5 }}>
                    <FileXIcon size={13} color={theme.palette.error.main} weight="fill" />
                    <Typography variant="textXS_medium" sx={{ color: 'error.main' }}>
                      <Trans
                        i18nKey="assessments.summary.removedFromParent"
                        values={{ version: parentVersion, count: removedDocuments.length }}
                      />
                    </Typography>
                  </Box>
                  {removedDocuments.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 1.25,
                        border: `1px solid ${theme.palette.error.light}`,
                        backgroundColor: 'error.lighter',
                        borderRadius: 1.25,
                      }}
                    >
                      <FileXIcon size={16} color={theme.palette.error.main} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="textSM_medium"
                          sx={{
                            color: 'text.tertiary',
                            textDecoration: 'line-through',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {doc.fileName}
                        </Typography>
                        <Typography variant="textXS_regular" sx={{ color: 'error.main' }}>
                          <Trans
                            i18nKey="assessments.summary.wasInParent"
                            values={{ version: parentVersion }}
                          />
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </>
              )}

              {missingStandardDocuments.length > 0 && (
                <>
                  {!isSkipped && domain.documents && domain.documents.length > 0 && (
                    <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                      <Trans i18nKey="assessments.summary.missingDocumentsSection" />
                    </Typography>
                  )}
                  {missingStandardDocuments.map((stdDoc) => (
                    <MissingStandardDocumentItem key={stdDoc.key} standardDocument={stdDoc} />
                  ))}
                </>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  },
);
